import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { useAuth } from "../context/AuthContext";
import { useWarehouses } from "../lib/useWarehouses";

const EMPTY = { email: "", name: "", password: "", role: "staff", warehouse_id: "" };

export default function UserFormDialog({ open, onOpenChange, user, onSubmit }) {
  const { user: currentUser } = useAuth();
  const [form, setForm] = useState(EMPTY);
  // iter 215 (2026-02): user_admin 不能创建/编辑 admin,也不能给别人升 admin。
  const callerIsUserAdmin = currentUser?.role === "user_admin";
  const editingSelf = user && currentUser && user.id === currentUser.id;
  const editingAnAdmin = user?.role === "admin";
  const roleLocked = callerIsUserAdmin && (editingSelf || editingAnAdmin);
  const { selectable: warehouses } = useWarehouses();

  useEffect(() => {
    if (user) {
      setForm({ email: user.email || "", name: user.name || "", password: "", role: user.role || "staff", warehouse_id: user.warehouse_id || "" });
    } else {
      setForm(EMPTY);
    }
  }, [user, open]);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = { email: form.email, name: form.name, role: form.role };
    if (form.role === "sample_warehouse") payload.warehouse_id = form.warehouse_id || null;
    if (form.password) payload.password = form.password;
    onSubmit(payload);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-2xl" data-testid="user-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading font-bold text-xl tracking-tight text-gray-900">
            {user ? "Edit User" : "Add User"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label className="text-xs font-medium text-gray-600">Email *</Label>
            <Input
              data-testid="user-form-email"
              type="email"
              required
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
              className="mt-1.5 rounded-lg border-gray-300 focus-visible:ring-brand-500"
            />
          </div>
          <div>
            <Label className="text-xs font-medium text-gray-600">Name</Label>
            <Input
              data-testid="user-form-name"
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              className="mt-1.5 rounded-lg border-gray-300 focus-visible:ring-brand-500"
            />
          </div>
          <div>
            <Label className="text-xs font-medium text-gray-600">
              {user ? "Reset Password (leave blank to keep unchanged)" : "Initial Password *"}
            </Label>
            <Input
              data-testid="user-form-password"
              type="password"
              required={!user}
              value={form.password}
              onChange={(e) => update("password", e.target.value)}
              placeholder={user ? "Leave blank to keep current password" : ""}
              className="mt-1.5 rounded-lg border-gray-300 focus-visible:ring-brand-500"
            />
          </div>
          <div>
            <Label className="text-xs font-medium text-gray-600">Role</Label>
            <Select value={form.role} onValueChange={(v) => update("role", v)} disabled={roleLocked}>
              <SelectTrigger data-testid="user-form-role" className={`mt-1.5 rounded-lg border-gray-300 focus:ring-brand-500 ${roleLocked ? "bg-gray-50 text-gray-500" : ""}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-lg">
                {/* iter 215: user_admin 看不到 "Admin" 选项 */}
                {!callerIsUserAdmin && <SelectItem value="admin">Admin</SelectItem>}
                <SelectItem value="user_admin">User Admin</SelectItem>
                <SelectItem value="bh_product_admin">BH Product Admin</SelectItem>
                <SelectItem value="sales_rep">Sales Rep</SelectItem>
                <SelectItem value="sample_warehouse">Sample Warehouse</SelectItem>
                <SelectItem value="ci_admin">CI Admin</SelectItem>
                <SelectItem value="staff">Staff</SelectItem>
              </SelectContent>
            </Select>
            {roleLocked && (
              <p className="text-[10px] text-gray-400 mt-1" data-testid="user-form-role-lock-note">
                {editingSelf ? "You cannot change your own role." : "User Admin cannot modify an Admin account."}
              </p>
            )}
          </div>
          {form.role === "sample_warehouse" && (
            <div>
              <Label className="text-xs font-medium text-gray-600">Warehouse *</Label>
              <Select value={form.warehouse_id || ""} onValueChange={(v) => update("warehouse_id", v)}>
                <SelectTrigger data-testid="user-form-warehouse" className="mt-1.5 rounded-lg border-gray-300 focus:ring-brand-500">
                  <SelectValue placeholder="Select warehouse" />
                </SelectTrigger>
                <SelectContent className="rounded-lg">
                  {warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <DialogFooter className="pt-1">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-lg border-gray-300">Cancel</Button>
            <Button type="submit" data-testid="user-form-submit" className="rounded-lg bg-[#1677ff] hover:bg-[#0958d9] transition-colors">
              {user ? "Save Changes" : "Create User"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
